// src/hooks/useCustomValidationStatus.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useCallback, useEffect, useState, useRef } from 'react';
import { useSimpleFormContext } from 'components/SimpleComponents/SimpleFormProvider';
import translateX from 'utils/translateX';
import { useModuleContext } from 'components/core/ModuleProvider';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const useCustomValidationStatus = (ctx, validationFn = () => true) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const {
    elementRef,
    required: requiredProps,
    onChange,
    loading = false,
    flattenFieldName,
    minLength,
    maxLength,
    args = {},
    isPhone = false,
  } = ctx || {};
  const { fieldsWithError, shouldShowErrors, requiredFields } = useSimpleFormContext();
  const { documentDuplicated } = useModuleContext();
  const required = requiredProps ?? requiredFields?.includes(flattenFieldName);

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  // Stabilize unstable inputs: the defaults args ({}) and validationFn (() => true)
  // create a NEW reference on every render. As effect/callback deps they made the
  // validation effect fire every render and loop on setState ("Maximum update depth
  // exceeded"). Refs hold the latest value; argsKey is a stable dependency.
  const validationFnRef = useRef(validationFn);
  validationFnRef.current = validationFn;
  const argsRef = useRef(args);
  argsRef.current = args;
  const argsKey = JSON.stringify(args ?? {});

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [customValidationMessage, setCustomValidationMessage] = useState('');
  const [customValidationStatus, setCustomValidationStatus] = useState('');
  const [shouldValidate, setShouldValidate] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (documentDuplicated) setShouldValidate(true);
  }, [documentDuplicated]);

  // Activate validation when form shows errors (after submit attempt)
  useEffect(() => {
    if (shouldShowErrors) setShouldValidate(true);
  }, [shouldShowErrors]);

  // Activate validation when args change (e.g., country code changes)
  useEffect(() => {
    const value =
      elementRef?.current?.value ||
      elementRef?.current?.input?.value ||
      elementRef?.current?.target?.value ||
      elementRef?.current?.resizableTextArea?.textArea?.value;

    // If there's a value and country changes, activate validation
    if (value && args?.countryCode) {
      setShouldValidate(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [args?.countryCode]);

  useEffect(() => {
    let value =
      elementRef?.current?.value ||
      elementRef?.current?.input?.value ||
      elementRef?.current?.target?.value ||
      elementRef?.current?.resizableTextArea?.textArea?.value;

    // For phone fields, extract only numbers from the formatted value
    // This ensures validation works correctly with formatted phone numbers like "(19) 98232-0000"
    if (isPhone && value) {
      value = value.replace(/\D/g, '');
    }

    const fieldId = elementRef?.current?.input?.id;

    if (loading) {
      setCustomValidationStatus('validating');
      setCustomValidationMessage('');
      return;
    }

    // Only skip validation if shouldValidate is EXPLICITLY false AND there's no value
    // This allows validation to run when country changes even before submit
    // IMPORTANT: Use strict comparison to avoid skipping when shouldValidate is undefined
    if (shouldValidate === false && !value) return;

    if (!value && required) {
      setCustomValidationStatus('error');
      setCustomValidationMessage(translateX('required_field'));
      return;
    }

    if (!value) {
      setCustomValidationStatus('');
      setCustomValidationMessage('');
      return;
    }

    // Validate minLength
    if (minLength && value.length < minLength) {
      setCustomValidationStatus('error');
      setCustomValidationMessage(translateX('min_length_error').replace('%MIN%', minLength));
      return;
    }

    // Validate maxLength
    if (maxLength && value.length > maxLength) {
      setCustomValidationStatus('error');
      setCustomValidationMessage(translateX('max_length_error').replace('%MAX%', maxLength));
      return;
    }

    const messageFromContext = fieldsWithError?.[fieldId] || '';
    const ok = validationFnRef.current?.(value, argsRef.current);

    if (!ok) {
      // validator failed: use contextual message or default 'invalid_data'
      setCustomValidationStatus('error');
      setCustomValidationMessage(translateX(messageFromContext || 'invalid_data'));
      return;
    }

    // valid case - return success status when validation passes
    setCustomValidationStatus('success');
    setCustomValidationMessage('');
  }, [
    loading,
    shouldValidate,
    shouldShowErrors,
    fieldsWithError,
    elementRef,
    required,
    minLength,
    maxLength,
    argsKey,
    isPhone,
  ]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  // PERFORMANCE FIX: Use debounce ref to avoid running validation on every keystroke
  const validationDebounceRef = useRef(null);

  const handleChange = useCallback(
    (...allArgs) => {
      // allArgs can be:
      // - Standard input: (ev)
      // - PhoneInput: (ev, profile, metaObj)
      // - Other custom inputs with multiple args

      const ev = allArgs[0];
      const value = ev?.target?.value ?? ev?.currentTarget?.value ?? ev ?? '';

      setShouldValidate(true);

      // PERFORMANCE FIX: Clear previous debounce timer
      if (validationDebounceRef.current) {
        clearTimeout(validationDebounceRef.current);
      }

      // PERFORMANCE FIX: Immediate feedback for empty field (fast check)
      if (!value) {
        const status = required ? 'error' : '';
        setCustomValidationStatus(status);
        setCustomValidationMessage(required ? translateX('required_field') : '');
        // IMPORTANT: Pass all original arguments to onChange
        onChange?.(...allArgs);
        return;
      }

      // PERFORMANCE FIX: Set neutral status immediately while typing
      // This prevents expensive validation on every keystroke
      setCustomValidationStatus('');
      setCustomValidationMessage('');

      // PERFORMANCE FIX: Debounce the actual validation (300ms)
      // This runs the expensive validationFn only after user stops typing
      validationDebounceRef.current = setTimeout(() => {
        // Validate minLength
        if (minLength && value.length < minLength) {
          setCustomValidationStatus('error');
          setCustomValidationMessage(translateX('min_length_error').replace('%MIN%', minLength));
          return;
        }

        // Validate maxLength
        if (maxLength && value.length > maxLength) {
          setCustomValidationStatus('error');
          setCustomValidationMessage(translateX('max_length_error').replace('%MAX%', maxLength));
          return;
        }

        const isValid = validationFnRef.current?.(value, argsRef.current);
        if (!isValid) {
          setCustomValidationStatus('error');
          setCustomValidationMessage(translateX('invalid_data'));
        } else {
          setCustomValidationStatus('success');
          setCustomValidationMessage('');
        }
      }, 300);

      // IMPORTANT: Pass all original arguments to onChange
      // This allows Form.Item's getValueFromEvent to receive all args
      onChange?.(...allArgs);
    },
    [required, minLength, maxLength, onChange, argsKey]
  );

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (validationDebounceRef.current) {
        clearTimeout(validationDebounceRef.current);
      }
    };
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return {
    customValidationStatus,
    // only show a message after a submit attempt (shouldShowErrors === true)
    customValidationMessage,
    handleChange,
  };
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default useCustomValidationStatus;

// *******************************************************************************
// HELPERS
// *******************************************************************************
